import { format } from "date-fns";
import { http } from "@/api/http";

const GOLD = [201, 168, 76];
const DARK = [12, 20, 34];
const CARD = [26, 37, 53];
const INK = [38, 44, 54];
const MUTED = [110, 122, 140];
const PAGE = { width: 210, height: 297, margin: 18 };

const latin = (text) => text.replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"')
  .replace(/[\u2013\u2014]/g, "-").replace(/\u2022/g, "-")
  .replace(/[^\u0009\u000a\u000d\u0020-\u007e\u00a0-\u00ff]/g, "").trimEnd();

const fetchPhoto = async (url) => {
  const response = await http.get(url.replace(/^\/api/, ""), { responseType: "blob" });
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(response.data);
  });
};

const loadPhotos = async (urls = []) => {
  const results = await Promise.all(urls.slice(0, 3).map((url) => fetchPhoto(url).catch(() => null)));
  return results.filter(Boolean);
};

// Markdown -> flat blocks the PDF renderer can lay out.
const parseBlocks = (markdown) => latin(markdown).split("\n").map((line) => {
  const raw = line.trim();
  if (!raw) return null;
  const heading = raw.match(/^(#{1,6})\s*(.+)$/);
  if (heading) return { type: heading[1].length <= 2 ? "h1" : "h2", text: heading[2].replace(/[*_`]/g, "").trim() };
  const bullet = raw.match(/^([-*•]|\d+[.)])\s+(.+)$/);
  if (bullet) return { type: "li", text: bullet[2].replace(/[*_`]/g, "").trim() };
  return { type: "p", text: raw.replace(/[*_`#]/g, "").trim() };
}).filter(Boolean);

const cover = (doc, { title, subtitle, userName, date, photosBefore, photosAfter, labels }) => {
  doc.setFillColor(...DARK);
  doc.rect(0, 0, PAGE.width, PAGE.height, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, PAGE.width, 4, "F");

  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold").setFontSize(30);
  doc.text("Skinly Check", PAGE.margin, 52);
  doc.setFont("helvetica", "normal").setFontSize(11);
  doc.setTextColor(150, 165, 190);
  doc.text(latin(labels.tagline), PAGE.margin, 62);

  doc.setDrawColor(...GOLD).setLineWidth(0.6);
  doc.line(PAGE.margin, 70, PAGE.width - PAGE.margin, 70);

  doc.setTextColor(240, 230, 208).setFont("helvetica", "bold").setFontSize(19);
  doc.text(doc.splitTextToSize(latin(title), PAGE.width - PAGE.margin * 2), PAGE.margin, 86);
  doc.setFont("helvetica", "normal").setFontSize(11).setTextColor(150, 165, 190);
  if (subtitle) doc.text(latin(subtitle), PAGE.margin, 102);
  if (userName) doc.text(latin(userName), PAGE.margin, 110);
  doc.text(date, PAGE.margin, 118);

  let y = 136;
  [[labels.before, photosBefore], [labels.after, photosAfter]].forEach(([label, photos]) => {
    if (!photos.length) return;
    doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(...GOLD);
    doc.text(latin(label), PAGE.margin, y);
    let x = PAGE.margin;
    photos.forEach((photo) => {
      doc.setFillColor(...CARD);
      doc.rect(x, y + 4, 52, 52, "F");
      try {
        doc.addImage(photo, "JPEG", x + 1, y + 5, 50, 50);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") console.warn("PDF: foto ignorada", error);
      }
      x += 58;
    });
    y += 68;
  });

  doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(120, 135, 160);
  doc.text(doc.splitTextToSize(latin(labels.coverDisclaimer), PAGE.width - PAGE.margin * 2), PAGE.margin, 272);
};

const contentPages = (doc, blocks, footer) => {
  let y = 0;
  const newPage = () => {
    doc.addPage();
    doc.setFillColor(252, 250, 246);
    doc.rect(0, 0, PAGE.width, PAGE.height, "F");
    doc.setFillColor(...GOLD);
    doc.rect(0, 0, PAGE.width, 2.5, "F");
    doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(...GOLD);
    doc.text("Skinly Check", PAGE.margin, 13);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...MUTED);
    doc.text(latin(footer), PAGE.width - PAGE.margin, 13, { align: "right" });
    y = 26;
  };
  newPage();

  const styles = {
    h1: { size: 15, font: "bold", color: GOLD, before: 6, after: 3, indent: 0 },
    h2: { size: 12, font: "bold", color: INK, before: 5, after: 2, indent: 0 },
    p: { size: 10, font: "normal", color: INK, before: 0, after: 2.5, indent: 0 },
    li: { size: 10, font: "normal", color: INK, before: 0, after: 2.5, indent: 5 },
  };

  blocks.forEach((block) => {
    const style = styles[block.type];
    const width = PAGE.width - PAGE.margin * 2 - style.indent;
    doc.setFont("helvetica", style.font).setFontSize(style.size).setTextColor(...style.color);
    const lines = doc.splitTextToSize((block.type === "li" ? "- " : "") + block.text, width);
    const lineHeight = style.size * 0.52;
    if (y + style.before + lines.length * lineHeight > PAGE.height - 22) newPage();
    y += style.before;
    if (block.type === "h1") {
      doc.setDrawColor(...GOLD).setLineWidth(0.4);
      doc.line(PAGE.margin, y - 3.5, PAGE.width - PAGE.margin, y - 3.5);
    }
    doc.text(lines, PAGE.margin + style.indent, y);
    y += lines.length * lineHeight + style.after;
  });

  const total = doc.getNumberOfPages();
  for (let page = 2; page <= total; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...MUTED);
    doc.text(`${page - 1}/${total - 1}`, PAGE.width / 2, PAGE.height - 10, { align: "center" });
  }
};

export const exportBrandedReport = async ({ title, subtitle, userName, text, filename, labels,
                                            photosBefore = [], photosAfter = [] }) => {
  const { jsPDF } = await import("jspdf");
  const [before, after] = await Promise.all([loadPhotos(photosBefore), loadPhotos(photosAfter)]);
  const doc = new jsPDF();
  const date = format(new Date(), "dd/MM/yyyy HH:mm");
  cover(doc, { title, subtitle, userName, date, photosBefore: before, photosAfter: after, labels });
  contentPages(doc, parseBlocks(text), `${latin(title)} - ${date}`);
  doc.save(`${filename}.pdf`);
};

export const exportTextReport = async ({ title, text, filename, subtitle = "" }) => {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  doc.setFontSize(16); doc.text(doc.splitTextToSize(latin(title), 170), 20, 20);
  doc.setFontSize(10); doc.text(subtitle, 20, 35);
  doc.setFontSize(11);
  let y = 47;
  doc.splitTextToSize(latin(text).replace(/#{1,6}\s?/g, "").replace(/\*/g, ""), 170).forEach((line) => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.text(line, 20, y); y += 6;
  });
  doc.save(`${filename}.pdf`);
};

export const exportImcReport = ({ result, history, t, locale }) => {
  const current = result ? `${t("imc.yourImc")}: ${result.imc} — ${t(`imc.classifications.${result.classification.key}`)}\n${t("imc.weight")}: ${result.weight} kg | ${t("imc.height")}: ${result.height} cm\n\n` : "";
  const rows = history.slice(0, 20).map((record) => `${format(new Date(record.created_date), "dd/MM/yyyy", { locale })} — ${t("imc.weight")}: ${record.weight} kg | ${t("imc.imcLabel")}: ${record.imc} | ${record.classification}`);
  return exportTextReport({ title: t("imc.title"), text: current + rows.join("\n"), filename: "imc-report" });
};
